/* eslint no-console: off */

import {config} from './config.js';
import {api} from '../api/api.js';

/**
 * @enum {string}
 */
const SEVERITY = {
    // These string values must match the expected severity values in the logging API.
    DEBUG: 'Debug',
    WARNING: 'Warning',
    ERROR: 'Error',
    CRITICAL: 'Critical'
};

function _outputLog(severity, name, message, data) {
    if (severity === SEVERITY.CRITICAL) {
        name = `${name}[${SEVERITY.CRITICAL}]`;
    }

    const text = message ? `${name}: ${message}` : name;

    switch (severity) {
        case SEVERITY.WARNING:
        case SEVERITY.ERROR:
        case SEVERITY.CRITICAL:
            if (data) console.error(text, data);
            else console.error(text);
            break;

        case SEVERITY.DEBUG:
        default:
            if (data) console.log(text, data);
            else console.log(text);
            break;
    }
}

function _recordLog(severity, name, message, data, path) {
    if (severity === SEVERITY.DEBUG)
        return; // Abort if debug log, we don't bother recording debug logs.

    if (data) {
        if (typeof data !== 'string')
            data = JSON.stringify(data);

        message = `${message}\n${data}`;
    }

    // Send the log to server to be recorded.
    api.log({
        Severity: severity,
        Name: name,
        Message: message,
        Path: path,
    }).catch(ex => {
        // Don't use log.error since that's likely just going to fail again, causing infinite recursion.
        console.error('log._recordLog failed: ' + ex.message);
    });
}

/**
 * Wrapper for logging that checks validity of arguments.
 * 
 * EXAMPLE OVERLOADS:
 * performLog(severity, name, message, data)
 * performLog(severity, name, message)
 * performLog(severity, name, data)
 * performLog(severity, name)
 * 
 * @param {SEVERITY} severity The severity level for the log.
 * @param {string} name The log name, aka the unique call/trace identifier, used so we know where to look in code for the source of the log. Eg: 'thefile.theFunction{anOptionalSection}'.
 * @param {string} [message] The message to log, this should be short and to the point, just the basic error and any (non-sensitive) data that might be useful. Don't put extra words to be elequent, or explain what the code does since we can just look at the code.
 * @param {any} [data] Optional data that will be appended to the log message. If this is an object it will be stringified, if an Error it will be output appropriately.
 */
function _performLog(severity, name, message, data) {
    // Abort if debug severity but not debug mode (don't log).
    if (!config.isDebug && severity === SEVERITY.DEBUG)
        return;

    // If Error (exception) object then use the message property within it.
    if (message instanceof Error) { message = message.message; }
    if (data instanceof Error) { data = data.message; }

    // Adjust per argument overloading...
    if (typeof data === 'undefined' && typeof message !== 'string') {
        data = message;
        message = void 0;
    }

    // If message is not already a string then make it one...
    if (typeof message !== 'string') {
        message = JSON.stringify(message);
    }

    _outputLog(severity, name, message, data);
    _recordLog(severity, name, message, data, document.location.href);
}

/**
 * Create a DEBUG log entry, this will only be sent when in debug (development) mode.
 * 
 * @param {string} name The log name, aka the unique call/trace identifier, used so we know where to look in code for the source of the log. Eg: 'thefile.theFunction{anOptionalSection}'.
 * @param {string} [message] The message to log, this should be short and to the point, just the basic error and any (non-sensitive) data that might be useful. Don't put extra words to be elequent, or explain what the code does since we can just look at the code.
 * @param {any} [data] Optional data that will be appended to the log message. If this is an object it will be stringified, if an Error it will be output appropriately.
 */
function _logDebug(name, message, data) {
    return _performLog(SEVERITY.DEBUG, name, message, data);
}

/**
 * Create a WARNING log entry.
 * 
 * @param {string} name The log name, aka the unique call/trace identifier, used so we know where to look in code for the source of the log. Eg: 'thefile.theFunction{anOptionalSection}'.
 * @param {string} [message] The message to log, this should be short and to the point, just the basic error and any (non-sensitive) data that might be useful. Don't put extra words to be elequent, or explain what the code does since we can just look at the code.
 * @param {any} [data] Optional data that will be appended to the log message. If this is an object it will be stringified, if an Error it will be output appropriately.
 */
function _logWarning(name, message, data) {
    return _performLog(SEVERITY.WARNING, name, message, data);
}

/**
 * Create an ERROR log entry.
 * 
 * @param {string} name The log name, aka the unique call/trace identifier, used so we know where to look in code for the source of the log. Eg: 'thefile.theFunction{anOptionalSection}'.
 * @param {string} [message] The message to log, this should be short and to the point, just the basic error and any (non-sensitive) data that might be useful. Don't put extra words to be elequent, or explain what the code does since we can just look at the code.
 * @param {any} [data] Optional data that will be appended to the log message. If this is an object it will be stringified, if an Error it will be output appropriately.
 */
function _logError(name, message, data) {
    return _performLog(SEVERITY.ERROR, name, message, data);
}

/**
 * Create a CRITICAL log entry.
 * 
 * @param {string} name The log name, aka the unique call/trace identifier, used so we know where to look in code for the source of the log. Eg: 'thefile.theFunction{anOptionalSection}'.
 * @param {string} [message] The message to log, this should be short and to the point, just the basic error and any (non-sensitive) data that might be useful. Don't put extra words to be elequent, or explain what the code does since we can just look at the code.
 * @param {any} [data] Optional data that will be appended to the log message. If this is an object it will be stringified, if an Error it will be output appropriately.
 */
function _logCritical(name, message, data) {
    return _performLog(SEVERITY.CRITICAL, name, message, data);
}

export const log = {
    debug: _logDebug,
    error: _logError,
    critical: _logCritical
};